import React, { useMemo } from 'react'
import { Button, Icon, Tag } from 'antd'

import {
  formatCount,
  getGeneratedAssets,
  getNextPhaseLabel,
  getPhaseCount,
  getReferenceAssets,
  getStoryboardPlanCount,
  getPhaseView,
  isEntityVerified,
  isStoryboardPlanApproved
} from '../../utils'
import AssetPreview from '../AssetPreview'
import EntityListPreview from '../EntityListPreview'
import PhaseSummaryCard from '../PhaseSummaryCard'
import ReferenceRequestCard from '../ReferenceRequestCard'
import StoryboardPlanPreview from '../StoryboardPlanPreview'
import { FinalizedReport } from '../../FinalizedReportModal'
import './PhaseWorkspace.scss'

const getEntityPrimaryAction = ({
  currentPhase,
  phaseData,
  phaseSteps,
  onExtractPhase,
  onVerifyAllPhase,
  onAdvancePhase
}) => {
  const view = getPhaseView(currentPhase)
  const currentPhaseData = phaseData[currentPhase] || {}
  const assets = getGeneratedAssets(phaseData, currentPhase)
  const hasExtracted = Boolean(
    currentPhaseData.extracted || currentPhaseData.count
  )
  const hasGenerated = Boolean(assets.length)
  const entities = currentPhaseData.entities || []
  const allEntitiesVerified =
    !entities.length || entities.every(isEntityVerified)

  if (!hasExtracted) {
    return {
      label: `Extract ${view.primaryNoun}`,
      icon: 'scan',
      loadingKey: `extract:${currentPhase}`,
      onClick: onExtractPhase
    }
  }

  if (!allEntitiesVerified) {
    return {
      label: `Verify all ${view.primaryNoun}`,
      icon: 'check-circle',
      loadingKey: `verify-all:${currentPhase}`,
      disabled: !onVerifyAllPhase,
      onClick: () => onVerifyAllPhase(currentPhase)
    }
  }

  if (!hasGenerated) {
    return null
  }

  return {
    label: `Continue to ${getNextPhaseLabel(phaseSteps, currentPhase)}`,
    icon: 'arrow-right',
    loadingKey: `advance:${currentPhase}`,
    onClick: onAdvancePhase
  }
}

const getStoryboardPrimaryAction = ({
  phaseData,
  phaseSteps,
  onGenerateStoryboardPlan,
  onAdvancePhase
}) => {
  const storyboardData = phaseData.storyboard || {}
  const assets = getGeneratedAssets(phaseData, 'storyboard')
  const hasPlan = getStoryboardPlanCount(storyboardData.plan) > 0
  const hasGenerated = Boolean(assets.length)

  if (!hasPlan) {
    return {
      label: 'Extract plan',
      icon: 'profile',
      loadingKey: 'storyboard:plan',
      onClick: onGenerateStoryboardPlan
    }
  }

  if (!hasGenerated) {
    return null
  }

  return {
    label: `Continue to ${getNextPhaseLabel(phaseSteps, 'storyboard')}`,
    icon: 'arrow-right',
    loadingKey: 'advance:storyboard',
    onClick: onAdvancePhase
  }
}

const getPhasePrimaryAction = ({
  currentPhase,
  phaseData,
  phaseSteps,
  onExtractPhase,
  onVerifyAllPhase,
  onGenerateStoryboardPlan,
  onAdvancePhase
}) => {
  const isEntityPhase = ['characters', 'outfits', 'locations'].includes(
    currentPhase
  )

  if (isEntityPhase) {
    return getEntityPrimaryAction({
      currentPhase,
      phaseData,
      phaseSteps,
      onExtractPhase,
      onVerifyAllPhase,
      onAdvancePhase
    })
  }

  if (currentPhase === 'storyboard') {
    return getStoryboardPrimaryAction({
      phaseData,
      phaseSteps,
      onGenerateStoryboardPlan,
      onAdvancePhase
    })
  }

  return null
}

const ACTIVE_GENERATION_STATUSES = [
  'pending',
  'queued',
  'processing',
  'running',
  'in_progress'
]
const SUCCESS_GENERATION_STATUSES = ['success', 'completed', 'done']
const FAILED_GENERATION_STATUSES = ['failed', 'error']

const getShortTaskId = taskId => {
  const value = String(taskId || '').trim()
  if (!value) return ''
  return value.length > 16 ? `${value.slice(0, 8)}...${value.slice(-4)}` : value
}

const getGenerationStatusMeta = ({
  currentPhase,
  generationTask,
  generationProgress,
  generationStatus,
  generationMessage,
  hasGeneratedAssets,
  isActivePhase,
  phaseBusyAction,
  view
}) => {
  const taskStatus = String(generationTask?.status || '').toLowerCase()
  const liveStatus = String(generationStatus || '').toLowerCase()
  const busyForGeneration = phaseBusyAction === `generate:${currentPhase}`
  const isRefreshingStatus =
    isActivePhase && phaseBusyAction === 'generation-status'
  const taskId = generationTask?.taskId
  const status = liveStatus || taskStatus
  const isTaskActive = ACTIVE_GENERATION_STATUSES.includes(taskStatus)
  const shouldUseLiveStatus = busyForGeneration || isTaskActive
  const generatedLabel = view.generatedLabel || 'visuals'

  if (isRefreshingStatus) {
    return {
      tone: 'active',
      icon: 'sync',
      spin: true,
      label: 'Checking generation status',
      description: 'Refreshing the latest visual results for this phase.',
      taskId,
      progress: generationProgress
    }
  }

  if (
    FAILED_GENERATION_STATUSES.includes(taskStatus) ||
    (shouldUseLiveStatus && FAILED_GENERATION_STATUSES.includes(liveStatus))
  ) {
    return {
      tone: 'failed',
      icon: 'warning',
      label: 'Generation needs attention',
      description:
        generationTask?.error ||
        generationMessage ||
        'The visual generation task failed. Retry from the chat when ready.',
      taskId,
      status
    }
  }

  if (
    !hasGeneratedAssets &&
    (SUCCESS_GENERATION_STATUSES.includes(taskStatus) ||
      (shouldUseLiveStatus && SUCCESS_GENERATION_STATUSES.includes(liveStatus)))
  ) {
    return {
      tone: 'success',
      icon: 'check-circle',
      label: 'Generation completed',
      description: 'Visuals are ready. Refresh status if they do not appear yet.',
      taskId,
      status
    }
  }

  if (
    busyForGeneration ||
    isTaskActive ||
    (shouldUseLiveStatus && ACTIVE_GENERATION_STATUSES.includes(liveStatus))
  ) {
    return {
      tone: 'active',
      icon: 'loading',
      spin: true,
      label: `Generating ${generatedLabel}...`,
      description:
        generationMessage ||
        (taskId
          ? 'Generation is queued and Sharelook is polling for completed visuals.'
          : 'Sharelook is preparing the visual generation request.'),
      taskId,
      progress: generationProgress,
      status
    }
  }

  return null
}

const AnimatedEllipsisLabel = ({ label, animate = false }) => {
  const labelText = typeof label === 'string' ? label : ''
  const shouldAnimate = animate && labelText.endsWith('...')

  if (!shouldAnimate) return label

  return (
    <>
      {labelText.replace(/\.\.\.$/, '')}
      <span
        className="creative-direction-chat__generation-status-ellipsis"
        aria-hidden="true"
      >
        <span>.</span>
        <span>.</span>
        <span>.</span>
      </span>
    </>
  )
}

const GenerationStatusCard = ({ meta, compact = false }) => {
  if (!meta) return null

  const progress = Number(meta.progress || 0)
  const shouldShowProgress =
    Number.isFinite(progress) && progress > 0 && progress < 100
  const taskId = getShortTaskId(meta.taskId)
  const className = [
    'creative-direction-chat__generation-status',
    `creative-direction-chat__generation-status--${meta.tone}`,
    compact ? 'creative-direction-chat__generation-status--compact' : ''
  ]
    .filter(Boolean)
    .join(' ')
  const labelText = typeof meta.label === 'string' ? meta.label : ''
  const renderedLabel = (
    <AnimatedEllipsisLabel
      label={meta.label}
      animate={meta.tone === 'active'}
    />
  )

  if (compact) {
    return (
      <div className={className} aria-label={meta.label} title={meta.label}>
        <Icon
          type={meta.tone === 'active' ? 'loading' : meta.icon}
          spin={meta.tone === 'active' || meta.spin}
        />
      </div>
    )
  }

  return (
    <div className={className}>
      <div className="creative-direction-chat__generation-status-icon">
        <Icon type={meta.icon} spin={meta.spin} />
      </div>
      <div className="creative-direction-chat__generation-status-copy">
        <span>Visual generation</span>
        <strong aria-label={labelText || undefined}>{renderedLabel}</strong>
        <p>{meta.description}</p>
        {shouldShowProgress || taskId ? (
          <div className="creative-direction-chat__generation-status-meta">
            {shouldShowProgress ? <small>{progress}% complete</small> : null}
            {taskId ? <small>Task {taskId}</small> : null}
          </div>
        ) : null}
      </div>
      {meta.tone === 'active' ? (
        <div
          className="creative-direction-chat__generation-status-pulse"
          aria-hidden="true"
        >
          <span />
          <span />
          <span />
        </div>
      ) : null}
    </div>
  )
}

const GeneratedAssetsMarker = ({ assetCount, label }) => {
  if (!assetCount) return null

  return (
    <div className="creative-direction-chat__generated-marker">
      <span className="creative-direction-chat__generated-marker-icon">
        <Icon type="check-circle" />
      </span>
      <div>
        <strong>{formatCount(assetCount)} visuals ready</strong>
        <small>{label} are available for review.</small>
      </div>
    </div>
  )
}

const PhaseWorkspace = ({
  currentPhase,
  phaseSteps,
  phaseData,
  creativeGuideline,
  isFinalized,
  creativeStatement,
  hasConversation,
  isStreaming,
  isSynthesizingVision,
  isLockingVision,
  isVisionLocked,
  isActivePhase,
  isSelectedPhase,
  showActions = true,
  phaseBusyAction,
  acceptedExtensions,
  referenceUploadProgress,
  referenceUploadStatus,
  referenceUploadMessage,
  generationProgress,
  generationStatus,
  generationMessage,
  onExtractPhase,
  onVerifyEntity,
  onVerifyAllPhase,
  onGenerateStoryboardPlan,
  onRefreshGenerationStatus,
  onUploadRequestedReference,
  onAdvancePhase,
  onSynthesizeVision,
  onLockVision,
  onFinalizeCreativeDirection,
  onViewFinalizedReport,
  onTogglePhase,
  showReferenceRequestCard = true,
  isDrawerView = false
}) => {
  const view = getPhaseView(currentPhase)
  const isBusy = Boolean(phaseBusyAction)
  const currentPhaseData = phaseData[currentPhase] || {}
  const count = getPhaseCount(phaseData, currentPhase)
  const assets = getGeneratedAssets(phaseData, currentPhase)
  const referenceUploads = getReferenceAssets(phaseData, currentPhase)
  const hasGeneratedAssets = assets.length > 0
  const isEntityPhase = ['characters', 'outfits', 'locations'].includes(
    currentPhase
  )
  const isVisionPhase = currentPhase === 'vision'
  const isStoryboardPhase = currentPhase === 'storyboard'
  const isFinalPhase = currentPhase === 'finalized'
  const hasFinalizedReport = isFinalPhase && isFinalized && creativeGuideline
  const canFinalizeReport =
    isFinalPhase && isActivePhase && !isFinalized && onFinalizeCreativeDirection
  const hasVisionStatement = Boolean(String(creativeStatement || '').trim())
  const shouldShowFullPhase = isSelectedPhase
  const storyboardNeedsChat =
    isStoryboardPhase && isActivePhase && !hasGeneratedAssets
  const hasStoryboardPlan =
    isStoryboardPhase && getStoryboardPlanCount(currentPhaseData.plan) > 0
  const generationStatusMeta = getGenerationStatusMeta({
    currentPhase,
    generationTask: currentPhaseData.generationTask,
    generationProgress,
    generationStatus,
    generationMessage,
    hasGeneratedAssets,
    isActivePhase,
    phaseBusyAction,
    view
  })
  const renderGenerationStatus = ({ compact = false } = {}) => (
    <GenerationStatusCard meta={generationStatusMeta} compact={compact} />
  )
  const isGeneratingVisuals = generationStatusMeta?.tone === 'active'

  const primaryAction = useMemo(() => {
    if (!isActivePhase) return null

    return getPhasePrimaryAction({
      currentPhase,
      phaseData,
      phaseSteps,
      onExtractPhase,
      onVerifyAllPhase,
      onGenerateStoryboardPlan,
      onAdvancePhase
    })
  }, [
    currentPhase,
    isActivePhase,
    onAdvancePhase,
    onExtractPhase,
    onGenerateStoryboardPlan,
    onVerifyAllPhase,
    phaseData,
    phaseSteps
  ])

  const renderPhaseActions = ({ compact = false } = {}) => {
    if (
      !showActions ||
      (!primaryAction && !hasFinalizedReport && !canFinalizeReport)
    ) {
      return null
    }

    const actionRowClass = compact
      ? 'creative-direction-chat__phase-summary-actions'
      : 'creative-direction-chat__phase-action-row'

    return (
      <div className={actionRowClass}>
        {hasFinalizedReport && !isDrawerView ? (
          <Button icon="profile" onClick={onViewFinalizedReport}>
            View Finalized Report
          </Button>
        ) : null}
        {canFinalizeReport ? (
          <Button
            type="primary"
            className="creative-direction-chat__phase-action-button creative-direction-chat__phase-action-button--primary"
            icon="profile"
            loading={phaseBusyAction === 'finalize'}
            disabled={isBusy && phaseBusyAction !== 'finalize'}
            onClick={onFinalizeCreativeDirection}
          >
            Generate Creative Guideline Report
          </Button>
        ) : null}
        {primaryAction ? (
          <Button
            type="primary"
            className="creative-direction-chat__phase-action-button creative-direction-chat__phase-action-button--primary"
            icon={primaryAction.icon}
            loading={
              (primaryAction.loadingKey &&
                phaseBusyAction === primaryAction.loadingKey) ||
              String(phaseBusyAction || '').endsWith(`:${currentPhase}`)
            }
            disabled={
              primaryAction.disabled ||
              (isBusy && phaseBusyAction !== primaryAction.loadingKey)
            }
            onClick={primaryAction.onClick}
          >
            {primaryAction.label}
          </Button>
        ) : null}
      </div>
    )
  }

  const taskTitle = isFinalPhase
    ? isFinalized
      ? 'Creative guideline is ready'
      : 'Ready to build the creative guideline'
    : isVisionPhase
      ? isVisionLocked
        ? 'Vision statement locked'
        : hasVisionStatement
          ? 'Vision statement ready'
          : 'Prepare visual vision'
      : isGeneratingVisuals
        ? generationStatusMeta.label
        : isStoryboardPhase && !hasStoryboardPlan
          ? 'Plan storyboard in chat'
          : isStoryboardPhase && hasStoryboardPlan
            ? 'Review storyboard plan'
            : count
              ? `AI found ${formatCount(count)} ${view.primaryNoun}`
              : `Prepare ${view.primaryNoun}`

  const taskDescription = isFinalPhase
    ? 'Finalize everything into the document used by the shot list step.'
    : isVisionPhase
      ? 'Review the visual direction before moving into characters and production references.'
      : !isActivePhase
        ? 'You are reviewing this completed step. The current task remains below in the conversation.'
        : isGeneratingVisuals
          ? 'Sharelook is preparing visuals.'
          : isStoryboardPhase
            ? 'Extract the plan from the conversation, then review storyboard sheets when they are ready.'
            : 'Run each step in order. Sharelook will stop you only when something still needs approval.'

  if (!isActivePhase && !shouldShowFullPhase) {
    return (
      <PhaseSummaryCard
        phase={currentPhase}
        phaseData={phaseData}
        isFinalized={isFinalized}
        onTogglePhase={onTogglePhase}
      />
    )
  }

  if (isActivePhase && !shouldShowFullPhase) {
    return (
      <section className="creative-direction-chat__phase-summary creative-direction-chat__phase-summary--active">
        <div className="creative-direction-chat__phase-summary-icon">
          <Icon type={view.icon} />
        </div>
        <div className="creative-direction-chat__phase-summary-content">
          <span>{view.label}</span>
          <div className="creative-direction-chat__phase-summary-title-row">
            <strong aria-label={isGeneratingVisuals ? taskTitle : undefined}>
              <AnimatedEllipsisLabel
                label={taskTitle}
                animate={isGeneratingVisuals}
              />
            </strong>
          </div>
          <small>{taskDescription}</small>
        </div>
        {generationStatusMeta ? (
          <div className="creative-direction-chat__phase-summary-status">
            {renderGenerationStatus({ compact: true })}
          </div>
        ) : null}
        <div className="creative-direction-chat__phase-summary-controls">
          {renderPhaseActions({ compact: true })}
        </div>
      </section>
    )
  }

  if (isDrawerView && isVisionPhase) {
    return (
      <section className="creative-direction-chat__phase-drawer-panel creative-direction-chat__phase-workspace creative-direction-chat__vision-drawer-panel">
        <div className="creative-direction-chat__drawer-doc">
          <div className="creative-direction-chat__drawer-doc-section">
            <div className="creative-direction-chat__drawer-doc-title">
              <Icon type="bulb" />
              <span>Vision Statement</span>
              {isVisionLocked ? <Tag color="green">Saved</Tag> : null}
            </div>

            {hasVisionStatement ? (
              <div className="creative-direction-chat__vision-drawer-text">
                {creativeStatement}
              </div>
            ) : (
              <div className="creative-direction-chat__vision-empty creative-direction-chat__vision-empty--drawer">
                <Icon type="info-circle" />
                <span>
                  When the conversation feels ready, create the vision statement.
                </span>
              </div>
            )}
          </div>
        </div>

        {isActivePhase ? (
          <div className="creative-direction-chat__phase-action-row">
            <Button
              icon={hasVisionStatement ? 'reload' : 'star'}
              loading={isSynthesizingVision}
              disabled={
                isStreaming ||
                isVisionLocked ||
                isLockingVision ||
                !onSynthesizeVision
              }
              onClick={() => onSynthesizeVision({ showError: true })}
            >
              {hasVisionStatement
                ? 'Update vision statement'
                : 'Create vision statement'}
            </Button>
            <Button
              type="primary"
              icon="check-circle"
              loading={isLockingVision}
              disabled={
                isStreaming ||
                isVisionLocked ||
                !hasVisionStatement ||
                !onLockVision
              }
              onClick={onLockVision}
            >
              Lock vision
            </Button>
          </div>
        ) : null}
      </section>
    )
  }

  if (isDrawerView && isStoryboardPhase) {
    const planCount = getStoryboardPlanCount(currentPhaseData.plan)
    return (
      <section className="creative-direction-chat__phase-drawer-panel creative-direction-chat__phase-workspace">
        {renderGenerationStatus()}
        {hasGeneratedAssets ? (
          <section className="creative-direction-chat__drawer-section">
            <div className="creative-direction-chat__drawer-doc-title">
              <Icon type="picture" />
              <span>{`Generated Visuals (${assets.length})`}</span>
              <Button
                size="small"
                icon="sync"
                disabled={isBusy || !isActivePhase}
                loading={phaseBusyAction === 'generation-status'}
                onClick={onRefreshGenerationStatus}
              >
                Refresh
              </Button>
            </div>
            <AssetPreview
              assets={assets}
              previewAssets={assets}
              entities={currentPhaseData.entities || []}
              emptyLabel="Storyboard panel"
              variant="drawer-strip"
            />
          </section>
        ) : null}
        {hasStoryboardPlan ? (
          <div className="creative-direction-chat__drawer-doc">
            <div className="creative-direction-chat__drawer-doc-section">
              <div className="creative-direction-chat__drawer-doc-title">
                <Icon type="profile" />
                <span>Storyboard Plan</span>
                <Tag color="blue">{`${planCount} ${planCount === 1 ? 'shot' : 'shots'}`}</Tag>
              </div>
              <StoryboardPlanPreview
                plan={currentPhaseData.plan}
                committedPanels={currentPhaseData.committedPanels}
                planApproved={isStoryboardPlanApproved(phaseData)}
                showAll
              />
            </div>
          </div>
        ) : (
          <div className="creative-direction-chat__quiet-note">
            <Icon type="info-circle" />
            <span>No storyboard plan has been extracted yet.</span>
          </div>
        )}
        {renderPhaseActions()}
      </section>
    )
  }

  if (isDrawerView && isEntityPhase) {
    const entities = currentPhaseData.entities || []
    const verifiedCount = entities.filter(isEntityVerified).length

    return (
      <section className="creative-direction-chat__phase-drawer-panel creative-direction-chat__phase-workspace">
        {renderGenerationStatus()}
        <div className="creative-direction-chat__phase-drawer-scroll">
          {hasGeneratedAssets ? (
            <section className="creative-direction-chat__drawer-section">
              <div className="creative-direction-chat__drawer-doc-title">
                <Icon type="picture" />
                <span>{`Generated Visuals (${assets.length})`}</span>
                <Button
                  size="small"
                  icon="sync"
                  disabled={isBusy || !isActivePhase}
                  loading={phaseBusyAction === 'generation-status'}
                  onClick={onRefreshGenerationStatus}
                >
                  Refresh
                </Button>
              </div>
              <AssetPreview
                assets={assets}
                previewAssets={assets}
                entities={currentPhaseData.entities || []}
                emptyLabel={view.generatedLabel || 'Pending'}
                variant="drawer-strip"
              />
            </section>
          ) : null}

          {referenceUploads.length ? (
            <section className="creative-direction-chat__drawer-section">
              <div className="creative-direction-chat__drawer-doc-title">
                <Icon type="paper-clip" />
                <span>{`Uploaded References (${referenceUploads.length})`}</span>
              </div>
              <AssetPreview
                assets={referenceUploads}
                previewAssets={referenceUploads}
                entities={currentPhaseData.entities || []}
                emptyLabel="Uploaded reference"
                variant="drawer-strip"
              />
            </section>
          ) : null}

          <div className="creative-direction-chat__drawer-doc-title creative-direction-chat__drawer-doc-title--entity-list">
            <Icon type={view.icon} />
            <span>{view.label}</span>
            <Tag color="blue">{`${verifiedCount}/${entities.length} verified`}</Tag>
          </div>

          <EntityListPreview
            entities={entities}
            phase={currentPhase}
            isActivePhase={isActivePhase}
            phaseBusyAction={phaseBusyAction}
            onVerifyEntity={onVerifyEntity}
            showFullDescriptions
            generatedAssets={assets}
            referenceAssets={referenceUploads}
          />
        </div>
        {renderPhaseActions()}
      </section>
    )
  }

  if (isDrawerView && isFinalPhase) {
    return (
      <section className="creative-direction-chat__phase-drawer-panel creative-direction-chat__phase-workspace">
        <div className="creative-direction-chat__drawer-doc">
          <div className="creative-direction-chat__drawer-doc-section">
            <div className="creative-direction-chat__drawer-doc-title">
              <Icon type="profile" />
              <span>Creative Direction Guideline</span>
            </div>
            {hasFinalizedReport ? (
              <div className="creative-direction-chat__drawer-guideline-report">
                <FinalizedReport report={creativeGuideline} />
              </div>
            ) : (
              <div className="creative-direction-chat__drawer-empty">
                <Icon type="profile" />
                <span>The report will compile all visual references, character styles, and storyboard plans.</span>
              </div>
            )}
          </div>
        </div>
        {renderPhaseActions()}
      </section>
    )
  }

  return (
    <section className="creative-direction-chat__decision-panel creative-direction-chat__phase-workspace">
      <div className="creative-direction-chat__phase-hero">
        <div className="creative-direction-chat__phase-icon">
          <Icon type={view.icon} />
        </div>
        <div>
          <h3>{taskTitle}</h3>
          <p>{taskDescription}</p>
        </div>
        {storyboardNeedsChat ? (
          <Tag color="blue">Use chat</Tag>
        ) : !primaryAction ? (
          <Tag color="green">Complete</Tag>
        ) : null}
      </div>

      {storyboardNeedsChat ? (
        <div className="creative-direction-chat__quiet-note">
          <Icon type="info-circle" />
          <span>
            Use the buttons when you are ready, or keep chatting to refine the
            storyboard before moving forward.
          </span>
        </div>
      ) : null}

      {renderGenerationStatus()}

      {showReferenceRequestCard ? (
        <ReferenceRequestCard
          phase={currentPhase}
          referenceRequest={currentPhaseData.referenceRequest}
          acceptedExtensions={acceptedExtensions}
          phaseBusyAction={phaseBusyAction}
          uploadProgress={referenceUploadProgress}
          uploadStatus={referenceUploadStatus}
          uploadMessage={referenceUploadMessage}
          onUploadReference={onUploadRequestedReference}
        />
      ) : null}

      {currentPhase === 'characters' &&
        currentPhaseData.castDynamicsUpdated &&
        !hasGeneratedAssets ? (
        <div className="creative-direction-chat__quiet-note creative-direction-chat__quiet-note--success">
          <Icon type="check-circle" />
          <span>Cast dynamics are ready for character visuals.</span>
        </div>
      ) : null}

      {isStoryboardPhase && hasStoryboardPlan ? (
        <StoryboardPlanPreview
          plan={currentPhaseData.plan}
          committedPanels={currentPhaseData.committedPanels}
          planApproved={isStoryboardPlanApproved(phaseData)}
        />
      ) : null}

      <EntityListPreview
        entities={currentPhaseData.entities || []}
        phase={currentPhase}
        isActivePhase={isActivePhase}
        phaseBusyAction={phaseBusyAction}
        onVerifyEntity={onVerifyEntity}
      />

      {referenceUploads.length ? (
        <>
          <div className="creative-direction-chat__subheading">
            <span>
              {`Uploaded reference${referenceUploads.length === 1 ? '' : 's'}`}
            </span>
          </div>
          <AssetPreview
            assets={referenceUploads}
            entities={currentPhaseData.entities || []}
            emptyLabel="Uploaded reference"
          />
        </>
      ) : null}

      {!isFinalPhase && hasGeneratedAssets ? (
        <>
          <div className="creative-direction-chat__subheading">
            <span>Generated visuals</span>
            <Button
              size="small"
              icon="sync"
              disabled={isBusy || !isActivePhase}
              loading={phaseBusyAction === 'generation-status'}
              onClick={onRefreshGenerationStatus}
            >
              Refresh
            </Button>
          </div>
          <GeneratedAssetsMarker
            assetCount={assets.length}
            label={view.generatedLabel || 'Generated visuals'}
          />
          <AssetPreview
            assets={assets}
            entities={currentPhaseData.entities || []}
            emptyLabel={view.generatedLabel || 'Pending'}
          />
        </>
      ) : null}

      {isEntityPhase && currentPhaseData.count ? (
        <div className="creative-direction-chat__quiet-note">
          <Icon type="info-circle" />
          <span>
            Verify all items, or review them one by one before moving on to
            visuals.
          </span>
        </div>
      ) : null}

      {renderPhaseActions()}
    </section>
  )
}

export default PhaseWorkspace
